import React, { useRef, useState } from 'react';
import {
    Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails,
    Rating, Tooltip, IconButton, Skeleton, useTheme,
    Modal
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";

import { IoIosInformationCircleOutline } from 'react-icons/io';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DoneIcon from '@mui/icons-material/Done';
import CoursSampleImg from '../../../Assets/Images/course_sample.png';
import EducatorSampleImg from '../../../Assets/Images/educator_sample.png';
import { specialFont } from '../../../Infrastructure/Theme/fontFamily';
import { sampleCourseLisitingSx } from './styles';

const SampleCourseViewModal = ({ openSample, setOpenSample, }) => {
    const theme = useTheme()
    const SHOW_RATINGS = false
    const [imageLoaded, setImageLoaded] = useState(false);
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const profileVector = "https://via.placeholder.com/50?text=No+Image";
    const boxRef = useRef(null)

    const course = {
        title: "Intro to Software Development",
        courseImage: CoursSampleImg,
        avatar: EducatorSampleImg,
        firstName: "John",
        lastName: "Doe",
        objectives: {
            one: "Learn the fundamentals of programming",
            two: "Understand the software development process",
            three: "Learn what kind of skills are required for different developer roles",
            four: "Be able to write and run programs using a code editor",
            five: "Understand the characteristics of the most popular programming languages",
        },
        curriculum: [
            { title: "Introduction", description: "Overview of course and what to expect" },
            { title: "Fundamentals of Programming", description: "Syntax, tools, execution flow, and data types" },
            { title: "Object Oriented Programming ", description: "Classes, Objects, References, and Methods" },
            { title: "Programming Languages", description: "Overview of Java, Python, and Javascript" }
        ],
        prerequisites: {
            one: "No programming experience needed",
        },
        intended_learner: "Anyone interested in learning how to code from scratch.",
        description: `<div><strong>Why learn programming?</strong></div><div><br></div><div>Our world is connected by the Internet which creates the global market, the biggest one in the world. The Internet is made of&nbsp;billions of computers and every one of them needs software to work. Software is made by programmers so investing in learning programming seems a pretty solid decision.</div><div><br></div><div><strong>What is included?</strong></div><div><br></div><div>In this course you will learn about the&nbsp;<strong>workings of computers</strong>&nbsp;and the&nbsp;<strong>fundamentals of programming</strong>&nbsp;which is shared amongst many modern programming languages like variables, conditional statements, loops, error handling, etc.&nbsp;You will solve many programming exercises because learning programming requires practice.</div><div>You will learn the basics of&nbsp;<strong>object oriented programming</strong>&nbsp;which is a code structuring technique that helps to produce more maintainable and understandable code.&nbsp;</div><div>You will be aware of the&nbsp;<strong>tools</strong>&nbsp;that programmers use day by day like libraries, frameworks, package managers, build tools, unit tests, version control systems, pipelines and AI&nbsp;based tools.&nbsp;</div><div>You will learn the&nbsp;<strong>process</strong>&nbsp;of making applications and the software development lifecycle including different methodologies like waterfall or agile development.&nbsp;</div><div>You will learn the characteristics of different&nbsp;<strong>programming languages</strong>&nbsp;and will be familiar with the top six.&nbsp;</div><div>You will learn about the different<strong>&nbsp;developer roles</strong>&nbsp;like backend, frontend, mobile, game developers or automated testers and data scientists and machine learning engineers. You will learn what kind of tasks they work on and what skills they require in order to solve them.&nbsp;</div>`,
        cost: 300,
        duration: 16,
        classSchedule: {
            schedule1: {
                startDate: "2025-08-01T14:00:00Z",
                times: {
                    Friday: {
                        start: "6:00 AM",
                        end: "8:00 AM"
                    },
                    Saturday: {
                        start: "6:00 AM",
                        end: "8:00 AM"
                    },
                    Sunday: {
                        start: "6:00 AM",
                        end: "8:00 AM"
                    }
                }
            }
        },
        classCount: 5,
        maxLearners: 10
    };

    const {
        title, courseImage, avatar, firstName, lastName, cost,
        duration, classSchedule
    } = course;

    const { startDate } = classSchedule.schedule1;

    const date = new Date(startDate);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();


    return (
        <Modal open={openSample} onClose={() => setOpenSample(false)}>
            <Box sx={sampleCourseLisitingSx.modalBox}>
                <IconButton
                    onClick={() => setOpenSample(false)}
                    sx={sampleCourseLisitingSx.closeBtn}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={sampleCourseLisitingSx.scrollContainer}>
                    <Box sx={sampleCourseLisitingSx.contentWrapper}>
                        <Box>
                            <Box
                                sx={{
                                    ...sampleCourseLisitingSx.imageBox,
                                    border: `3px solid ${theme.palette.primary.main}`,
                                    borderBottom: `3px solid ${theme.palette.primary.main}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        ...sampleCourseLisitingSx.imgContainer,
                                        borderBottom: `3px solid ${theme.palette.primary.main}`
                                    }}
                                >
                                    {!imageLoaded && (
                                        <Skeleton variant="rectangular" width="100%" height="100%" />
                                    )}
                                    <img
                                        src={courseImage}
                                        alt="course"
                                        onLoad={() => setImageLoaded(true)}
                                        style={{
                                            objectFit: 'cover',
                                            height: '100%',
                                            width: '100%',
                                            aspectRatio: '16/9',
                                            display: imageLoaded ? 'block' : 'none'
                                        }}
                                    />
                                </Box>
                                <Box px={2} display="flex" flexDirection="column" gap={3}>
                                    <Typography variant="h1" color="primary" sx={sampleCourseLisitingSx.title}>{title}</Typography>

                                    <Box sx={sampleCourseLisitingSx.avatarBox}>
                                        {!avatarLoaded && (
                                            <Skeleton variant="circular" width={50} height={50} />
                                        )}
                                        <img
                                            src={avatar || profileVector}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = profileVector;
                                            }}
                                            onLoad={() => setAvatarLoaded(true)}
                                            width={50}
                                            height={50}
                                            alt="avatar"
                                            style={{
                                                border: `1px solid ${theme.palette.primary.main}`,
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                display: avatarLoaded ? 'block' : 'none'
                                            }}
                                        />
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '18px', textTransform: 'capitalize' }}>
                                                By {firstName} {lastName}
                                            </Typography>
                                            <Box sx={{ display: SHOW_RATINGS ? 'flex' : 'none' }}>
                                                <Rating sx={{ fontSize: '1.2rem' }} name="star-rating" value={5} precision={0.5} readOnly />
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '16px', color: theme.palette.primary.main }} mb={1}>
                                                Starts on: <span style={{ fontWeight: '800' }}>{month}/{day}/{year}</span>
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: '900', pt: 1, fontSize: '15px' }}>
                                                {/* eslint-disable-next-line eqeqeq */}
                                                Duration: {duration} {duration == "1" ? "Week" : "Weeks"}
                                            </Typography>
                                            <Typography sx={sampleCourseLisitingSx.costTypography}>
                                                Cost: ${cost || 0}
                                            </Typography>
                                        </Box>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            <Box display="flex" flexDirection="column" alignItems="center">
                                                <Box sx={sampleCourseLisitingSx.scheduleButtons}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        sx={sampleCourseLisitingSx.seeScheduleBtn}
                                                        onClick={() => boxRef.current.scrollIntoView({ behavior: 'smooth' })}
                                                    >
                                                        See Schedules
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        sx={sampleCourseLisitingSx.enrollButtonCard}
                                                    >
                                                        Enroll Now
                                                    </Button>
                                                </Box>
                                                <Typography sx={sampleCourseLisitingSx.firstClassGuarantee}>
                                                    <Tooltip title="Cancel anytime before the first class for a full refund">
                                                        <IconButton size="small"><IoIosInformationCircleOutline /></IconButton>
                                                    </Tooltip>
                                                    First Class Money Back Guarantee
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={sampleCourseLisitingSx.rightColumn}>
                            <Box>
                                <Typography variant="h6" color="primary" fontFamily={specialFont} sx={sampleCourseLisitingSx.learningObjectivesTitle}>Learning Objectives</Typography>
                                <Box display="flex" flexWrap="wrap" gap={2}>
                                    {Object.values(course.objectives).map((item, idx) => (
                                        <Box key={idx} sx={sampleCourseLisitingSx.objectiveItem}>
                                            <DoneIcon sx={{ color: theme.palette.primary.main, fontSize: 20, marginRight: 1 }} />
                                            <Typography fontWeight={400} fontSize={18}>{item}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="h6" color="primary" fontFamily={specialFont} fontWeight={700} pb={3}>Curriculum</Typography>
                                {course.curriculum.map((item, index) => (
                                    <Accordion key={index} sx={{ wordBreak: "break-word" }}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                                            aria-controls={`panel${index + 1}-content`}
                                            id={`panel${index + 1}-header`}
                                        >
                                            <Typography variant="subtitle1" fontSize={18}>
                                                {item.title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="subtitle2" fontSize={18}>{item.description}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>

                            <Box>
                                <Typography variant="h6" color="primary" fontFamily={specialFont} fontWeight={700} pb={3}>Requirements</Typography>
                                <ul style={{ listStyle: 'disc', paddingLeft: '1.2rem' }}>
                                    {Object.values(course.prerequisites).map((item, i) => (
                                        <li key={i} style={{ fontSize: '18px' }}>{item}</li>
                                    ))}
                                </ul>
                            </Box>

                            <Box>
                                <Typography variant="h6" color="primary" fontFamily={specialFont} fontWeight={700} pb={3}>Description</Typography>
                                <Box fontSize={18} dangerouslySetInnerHTML={{ __html: course.description }} />
                            </Box>

                            <Box>
                                <Typography variant="h6" color="primary" fontFamily={specialFont} fontWeight={700} pb={3}>Who this course is for</Typography>
                                <Typography fontSize={18} fontWeight={'normal'}>{course.intended_learner}</Typography>
                            </Box>
                            <Box ref={boxRef} pb={3} pr={3} width="100%">
                                <Typography
                                    variant="h6"
                                    fontFamily={specialFont}
                                    color="primary"
                                    sx={{ fontWeight: '700' }}
                                    pb={6}
                                    mt={6}
                                >
                                    Cohort Schedule
                                </Typography>

                                <Box display="flex" flexWrap="wrap" gap={4}>
                                    {Object.entries(course.classSchedule).map(([key, schedule], index) => {
                                        const date = new Date(schedule.startDate);
                                        const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;

                                        return (
                                            <Box width={{ xs: '100%', sm: '100%' }} key={`schedule-${key}`} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={4}>
                                                <Box display="flex" flexDirection="column" gap={2} width={{ sm: '60%' }}>
                                                    <Typography
                                                        textTransform="capitalize"
                                                        variant="h6"
                                                        color="primary"
                                                        sx={sampleCourseLisitingSx.startDateTypography}
                                                    >
                                                        Start Date: <span style={{ fontWeight: 800 }}>{formattedDate}</span>
                                                    </Typography>

                                                    {Object.entries(schedule.times).map(([day, time], i) => (
                                                        <Typography
                                                            key={`${schedule.startDate}-${day}`}
                                                            textTransform="capitalize"
                                                            variant="h6"
                                                            sx={{ fontWeight: 700, fontSize: '18px' }}
                                                        >
                                                            {day}: <span style={{ fontWeight: 600 }}>{time.start} - {time.end}</span>
                                                        </Typography>
                                                    ))}


                                                    <Typography
                                                        variant="h6"
                                                        sx={sampleCourseLisitingSx.durationTypography}
                                                        component={'p'}
                                                    >
                                                        Duration:{' '}
                                                        <Typography
                                                            component="span"
                                                            textTransform="capitalize"
                                                            variant="h6"
                                                            sx={{ fontWeight: 600, fontSize: '18px' }}
                                                        >
                                                            {course.duration} Weeks
                                                        </Typography>
                                                    </Typography>

                                                </Box>

                                                <Box key={index} display="flex" flexDirection="column" justifyContent={'center'} alignItems={'center'} gap={2} width={{ sm: '40%' }}>
                                                    <Button
                                                        variant="contained"
                                                        sx={sampleCourseLisitingSx.enrollButton}
                                                    >
                                                        ENROLL NOW
                                                    </Button>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                        </Box>
                    </Box >
                </Box>
            </Box>
        </Modal>
    );
};

export default SampleCourseViewModal;
